<template>
  <div
    class="highlights-slide-menu-parts"
    :class="{ 'highlights-slide-menu-parts--isPc': isPc }"
    @click="handleOverlayClicked"
  >
    <div class="highlights-slide-menu-parts__inner">
      <ul class="highlights-slide-menu-parts__list">
        <template v-for="menu in state.menuList[highlightSlideMenu]">
          <li v-if="menu.display" :key="menu.id" class="highlights-slide-menu-parts__item">
            <a
              v-if="menu.link"
              class="highlights-slide-menu-parts__button"
              :href="menu.link"
              target="_blank"
            >
              <span>{{ menu.name }}</span>
              <svg
                class="highlights-slide-menu-parts__button-icon"
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.8619 7.87265C12.987 7.74763 13.1565 7.67739 13.3333 7.67739C13.5101 7.67739 13.6797 7.74763 13.8047 7.87265C13.9298 7.99767 14 8.16724 14 8.34406V12.3441C14 12.8745 13.7893 13.3832 13.4142 13.7583C13.0391 14.1333 12.5304 14.3441 12 14.3441H4C3.46957 14.3441 2.96086 14.1333 2.58579 13.7583C2.21071 13.3832 2 12.8745 2 12.3441V4.34406C2 3.81362 2.21071 3.30491 2.58579 2.92984C2.96086 2.55477 3.46957 2.34406 4 2.34406H8C8.17681 2.34406 8.34638 2.41429 8.4714 2.53932C8.59643 2.66434 8.66667 2.83391 8.66667 3.01072C8.66667 3.18753 8.59643 3.3571 8.4714 3.48213C8.34638 3.60715 8.17681 3.67739 8 3.67739H4C3.82319 3.67739 3.65362 3.74763 3.5286 3.87265C3.40357 3.99767 3.33333 4.16724 3.33333 4.34406V12.3441C3.33333 12.5209 3.40357 12.6904 3.5286 12.8155C3.65362 12.9405 3.82319 13.0107 4 13.0107H12C12.1768 13.0107 12.3464 12.9405 12.4714 12.8155C12.5964 12.6904 12.6667 12.5209 12.6667 12.3441V8.34406C12.6667 8.16724 12.7369 7.99767 12.8619 7.87265Z"
                  fill="white"
                />
                <path
                  d="M11.72 3.67739H10.6667C10.4899 3.67739 10.3203 3.60715 10.1953 3.48213C10.0702 3.3571 10 3.18753 10 3.01072C10 2.83391 10.0702 2.66434 10.1953 2.53932C10.3203 2.41429 10.4899 2.34406 10.6667 2.34406H13.3333C13.5101 2.34406 13.6797 2.41429 13.8047 2.53932C13.9298 2.66434 14 2.83391 14 3.01072V5.67739C14 5.8542 13.9298 6.02377 13.8047 6.14879C13.6797 6.27382 13.5101 6.34406 13.3333 6.34406C13.1565 6.34406 12.987 6.27382 12.8619 6.14879C12.7369 6.02377 12.6667 5.8542 12.6667 5.67739V4.62406L8.47333 8.81072C8.41136 8.87321 8.33762 8.9228 8.25638 8.95665C8.17515 8.9905 8.08801 9.00792 8 9.00792C7.91199 9.00792 7.82486 8.9905 7.74362 8.95665C7.66238 8.9228 7.58864 8.87321 7.52667 8.81072C7.46418 8.74875 7.41459 8.67501 7.38074 8.59377C7.34689 8.51253 7.32947 8.4254 7.32947 8.33739C7.32947 8.24938 7.34689 8.16224 7.38074 8.081C7.41459 7.99976 7.46418 7.92603 7.52667 7.86406L11.72 3.67739Z"
                  fill="white"
                />
              </svg>
            </a>
            <button
              v-else
              class="highlights-slide-menu-parts__button"
              :class="[
                `highlights-slide-menu-parts__button--${menu.id}`,
                { 'highlights-slide-menu-parts__button--disabled': menu.isDisabled },
              ]"
              @click.stop="handleMenuClicked(menu.id, menu.isDisabled)"
            >
              <span>{{ menu.name }}</span>
              <span v-if="menu.id === 'download'" class="highlights-slide-menu-parts__note">{{
                downloadMemo
              }}</span>
            </button>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive } from '@vue/composition-api'
import DeviceInfo from '@/util/DeviceInfo'
import I18n from '@/locales/I18n'
import SceneMoviesDocument from '@/store/stores/collectionModule/documents/highlight/SceneMoviesDocument'

/**
 * 以下のうちどのスライドメニューを表示するかの型
 * - ハイライト
 * - ハイライトコメント
 * - ブロック/通報
 */
export type HighlightSlideMenuType = 'highlight' | 'highlightPc' | 'highlightComment' | 'userBlock'

/**
 * スライドメニューリストの型
 */
export type HighlightSlideMenuListType = {
  highlight: {
    id: string
    name: string
    display: boolean
    isDisabled: boolean
    link?: string
  }[]
  highlightComment: {
    id: string
    name: string
    display: boolean
    isDisabled: boolean
    link?: string
  }[]
  highlightPc: {
    id: string
    name: string
    display: boolean
    isDisabled: boolean
    link?: string
  }[]
  userBlock: { id: string; name: string; display: boolean; isDisabled: boolean; link?: string }[]
}
/**
 * このページのstateの型
 */
type StateType = {
  menuList: HighlightSlideMenuListType
}
/**
 * レース動画再生画面 ハイライト、ハイライトコメント スライドメニューパーツ
 */
export default defineComponent({
  name: 'HighlightsSlideMenuParts',
  props: {
    /**
     * 表示対象となるスライドメニュー
     */
    highlightSlideMenu: {
      type: String as PropType<HighlightSlideMenuType | null>,
      default: null,
    },
    /**
     * 動画集
     */
    sceneMovies: {
      type: Object as PropType<SceneMoviesDocument | null>,
      default: () => ({}),
    },
    /**
     * ハイライトの切り抜きが可能かどうか
     */
    canClipTargetOperationHighlightForPc: {
      type: Boolean,
      default: false,
    },
    /**
     * 通報フォームURL
     */
    reportFormUrl: {
      type: String,
      default: null,
    },
  },
  setup(props, { emit }) {
    const isPc = DeviceInfo.isForPC()

    const state = reactive<StateType>({
      menuList: {
        highlight: [
          {
            id: 'sns',
            name: I18n.tc('RaceVideoPage.highlights.menu.sns'),
            display: true,
            isDisabled: false,
          },
          {
            id: 'edit',
            name: I18n.tc('RaceVideoPage.highlights.menu.edit'),
            display: true,
            isDisabled: false,
          },
          {
            id: 'delete',
            name: I18n.tc('RaceVideoPage.highlights.menu.delete'),
            display: true,
            isDisabled: false,
          },
        ],
        highlightPc: [
          {
            id: 'download',
            name: I18n.tc('RaceVideoPage.highlights.menu.download'),
            display: !(
              props.sceneMovies &&
              (props.sceneMovies.status === 'InProgress' || props.sceneMovies.status === 'Complete')
            ),
            isDisabled: !props.canClipTargetOperationHighlightForPc,
          },
          {
            id: 'xAndInstagram',
            name: I18n.tc('RaceVideoPage.highlights.menu.OnlyX'),
            display: true,
            isDisabled: false,
          },
          {
            id: 'share',
            name: I18n.tc('RaceVideoPage.highlights.menu.share'),
            display: false,
            isDisabled: false,
          },
          {
            id: 'edit',
            name: I18n.tc('RaceVideoPage.highlights.menu.edit'),
            display: true,
            isDisabled: false,
          },
          {
            id: 'delete',
            name: I18n.tc('RaceVideoPage.highlights.menu.delete'),
            display: true,
            isDisabled: false,
          },
        ],
        highlightComment: [
          {
            id: 'edit',
            name: I18n.tc('RaceVideoPage.highlights.menu.edit'),
            display: true,
            isDisabled: false,
          },
          {
            id: 'delete',
            name: I18n.tc('RaceVideoPage.highlights.menu.delete'),
            display: true,
            isDisabled: false,
          },
        ],
        userBlock: [
          {
            id: 'report',
            name: I18n.tc('RaceVideoPage.highlights.menu.report'),
            link: props.reportFormUrl,
            display: true,
            isDisabled: false,
          },
          {
            id: 'block',
            name: I18n.tc('RaceVideoPage.highlights.menu.block'),
            display: true,
            isDisabled: false,
          },
        ],
      },
    })

    /** ダウンロードメモ */
    const downloadMemo = computed(() =>
      props.canClipTargetOperationHighlightForPc
        ? I18n.tc('RaceVideoPage.highlights.menu.downloadMemo')
        : I18n.tc('RaceVideoPage.highlights.menu.downloadMemoWhenLive'),
    )

    /**
     * クリックされたメニューを親に伝える
     */
    const handleMenuClicked = (menuId: string, isDisabled: boolean) => {
      if (isDisabled) {
        // disabled状態の場合、後続処理をしない
        return
      }

      if (props.highlightSlideMenu === 'highlight') {
        emit('onClickMenu', menuId)
      } else if (props.highlightSlideMenu === 'highlightComment') {
        emit('onClickCommentMenu', menuId)
      } else if (props.highlightSlideMenu === 'highlightPc') {
        emit('onClickHighlightPcMenu', menuId)
      } else if (props.highlightSlideMenu === 'userBlock') {
        emit('onClickBlockMenu', menuId)
      }
    }

    /**
     * オーバーレイがクリックされたことを親に伝える
     */
    const handleOverlayClicked = () => {
      emit('onClickOverlay', props.highlightSlideMenu)
    }

    return {
      isPc,
      state,
      downloadMemo,
      handleMenuClicked,
      handleOverlayClicked,
    }
  },
})
</script>

<style scoped lang="scss">
.highlights-slide-menu-parts {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 41;
  background-color: $black70;

  &__list {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    padding-bottom: 16px;
    background-color: $black20;
    border: 1px solid #4e4e4e;
    border-top-right-radius: 8px;
    border-top-left-radius: 8px;
    animation: slideUp 0.3s ease;
  }

  &__item {
    border-bottom: 1px solid $black80;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 49px;
    padding-right: 14px;
    padding-left: 10px;
    font-size: 14px;
    color: $white;
    letter-spacing: 0.02em;

    &--delete {
      color: $red;
    }

    &-icon {
      margin-left: auto;
    }

    &--disabled {
      color: #808080;
    }
  }

  &__note {
    font-size: 12px;
    color: $white70;
    line-height: 1.25;
    text-align: right;
    letter-spacing: 0.02em;
    white-space: pre-wrap;
  }

  .race-video-highlights-pane--landscape-primary &,
  .race-video-highlights-pane--landscape-secondary & {
    .highlights-slide-menu-parts__list {
      width: 230px;
      padding-bottom: 0;
    }
  }

  &--isPc {
    .highlights-slide-menu-parts__inner {
      position: absolute;
      top: 0;
      right: 0;
      overflow: hidden;
      width: 375px;
      height: calc(100% - 220px);
      padding: 0 10px;
    }

    .race-video-highlights-pane--landscape-primary & {
      .highlights-slide-menu-parts__list {
        right: 10px;
        width: calc(100% - 20px);
      }
    }
  }
}

@keyframes slideUp {
  0% {
    transform: translateY(100%);
  }

  100% {
    transform: translateY(0);
  }
}
</style>
